import type { Breadcrumb } from '@sentry/browser';

import { normalizeUUID } from './utils';

const MAX_BREADCRUMBS = 100;
const MAX_MESSAGE_LENGTH = 500;
const MAX_DATA_VALUE_LENGTH = 200;
const COALESCE_WINDOW_MS = 5_000;

const ALLOWED_DATA_KEYS = new Set([
	'action',
	'actionSubject',
	'actionSubjectId',
	'durationMs',
	'experience',
	'kind',
	'lifecycle',
	'method',
	'nextState',
	'operation',
	'outcome',
	'previousState',
	'repeatCount',
	'route',
	'screen',
	'state',
	'status',
	'surface',
	'touchpoint',
]);

type PendingCoalescedBreadcrumb = {
	key: string;
	breadcrumb: Breadcrumb;
	repeatCount: number;
	timer: ReturnType<typeof setTimeout>;
};

const truncate = (value: string, maxLength: number): string =>
	value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

const sanitizeString = (value: string, maxLength = MAX_DATA_VALUE_LENGTH): string => {
	const { normalized } = normalizeUUID(value);
	const withoutQuery = normalized.replace(/([a-z][a-z0-9+.-]*:\/\/[^?\s]+)\?[^\s]*/gi, '$1');
	return truncate(withoutQuery, maxLength);
};

const sanitizeData = (data: Breadcrumb['data']): Breadcrumb['data'] | undefined => {
	if (!data) {
		return;
	}

	const sanitized = Object.entries(data).reduce<Record<string, string | number | boolean>>(
		(result, [key, value]) => {
			if (
				!ALLOWED_DATA_KEYS.has(key) ||
				(value !== null &&
					typeof value !== 'string' &&
					typeof value !== 'number' &&
					typeof value !== 'boolean')
			) {
				return result;
			}

			if (typeof value === 'string') {
				const sanitizedValue = sanitizeString(value);
				result[key] = sanitizedValue;
			} else if (value !== null) {
				result[key] = value;
			}
			return result;
		},
		{},
	);

	return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

const sanitizeCategory = (category: string | undefined): string => {
	if (!category) {
		return 'conversation-assistant.custom';
	}

	const safeCategory = category
		.toLowerCase()
		.replace(/[^a-z0-9._-]+/g, '-')
		.slice(0, 80);
	return safeCategory.startsWith('conversation-assistant.')
		? safeCategory
		: `conversation-assistant.${safeCategory}`;
};

const isCoalescable = (breadcrumb: Breadcrumb): boolean =>
	breadcrumb.level === 'warning' ||
	breadcrumb.level === 'error' ||
	breadcrumb.category?.includes('store') === true;

export class ConversationAssistantBreadcrumbGateway {
	private breadcrumbs: Breadcrumb[] = [];
	private pendingCoalescedBreadcrumb: PendingCoalescedBreadcrumb | undefined;

	public add(crumb: string | Breadcrumb): void {
		const breadcrumb = this.sanitize(typeof crumb === 'string' ? { message: crumb } : crumb);

		if (!isCoalescable(breadcrumb)) {
			this.push(breadcrumb);
			return;
		}

		const key = JSON.stringify({
			category: breadcrumb.category,
			data: breadcrumb.data,
			level: breadcrumb.level,
			message: breadcrumb.message,
			type: breadcrumb.type,
		});
		const pending = this.pendingCoalescedBreadcrumb;

		if (pending?.key === key) {
			pending.repeatCount += 1;
			return;
		}

		this.flushCoalescedSummary();
		this.push(breadcrumb);
		this.pendingCoalescedBreadcrumb = {
			key,
			breadcrumb,
			repeatCount: 1,
			timer: setTimeout(() => this.flushCoalescedSummary(), COALESCE_WINDOW_MS),
		};
	}

	public snapshot(): Breadcrumb[] {
		this.flushCoalescedSummary();
		return this.breadcrumbs.map((breadcrumb) => ({
			...breadcrumb,
			data: breadcrumb.data ? { ...breadcrumb.data } : undefined,
		}));
	}

	public reset(): void {
		if (this.pendingCoalescedBreadcrumb) {
			clearTimeout(this.pendingCoalescedBreadcrumb.timer);
		}
		this.pendingCoalescedBreadcrumb = undefined;
		this.breadcrumbs = [];
	}

	private sanitize(breadcrumb: Breadcrumb): Breadcrumb {
		const category = sanitizeCategory(breadcrumb.category);
		const data = sanitizeData(breadcrumb.data);
		const message =
			typeof breadcrumb.message === 'string'
				? sanitizeString(breadcrumb.message, MAX_MESSAGE_LENGTH)
				: undefined;
		return {
			category,
			data,
			level: breadcrumb.level,
			message,
			timestamp: breadcrumb.timestamp ?? Date.now() / 1000,
			type: breadcrumb.type,
		};
	}

	private flushCoalescedSummary(): void {
		const pending = this.pendingCoalescedBreadcrumb;
		if (!pending) {
			return;
		}

		clearTimeout(pending.timer);
		this.pendingCoalescedBreadcrumb = undefined;
		if (pending.repeatCount <= 1) {
			return;
		}

		this.push({
			...pending.breadcrumb,
			data: {
				...pending.breadcrumb.data,
				repeatCount: pending.repeatCount,
			},
			message: pending.breadcrumb.message
				? `${pending.breadcrumb.message} (repeated ${pending.repeatCount} times)`
				: `Breadcrumb repeated ${pending.repeatCount} times`,
			timestamp: Date.now() / 1000,
		});
	}

	private push(breadcrumb: Breadcrumb): void {
		this.breadcrumbs.push(breadcrumb);
		if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
			this.breadcrumbs.splice(0, this.breadcrumbs.length - MAX_BREADCRUMBS);
		}
	}
}
